// SpringerEditSection — Click-to-Edit Modal-Bereich für bestehende Einteilungen
// Zeigt die aktuelle Hauptfunktion + die zugehörigen Sprungfunktionen mit
// Lösch-Buttons und einer "Springer anhängen"-Section mit verfügbaren
// Springer-Fahrzeugen.
import React, { useState } from 'react';
import { Plus, X, Truck, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const sfKey = (sf) => `${sf.fahrzeug_id}_${sf.funktion_kuerzel}`;

export function SpringerEditSection({
  existingData,
  springerFahrzeuge,
  mitarbeiter,
  diensttag,
  fahrzeugTypNummern,
  istPraktikant,
  onSave,
  onClose,
}) {
  const [isPanelOpen, setPanelOpen] = useState(false);
  const [isSaving, setSaving] = useState(false);
  const [draftSpringer, setDraftSpringer] = useState(
    (existingData?.sprungfunktionen || []).map(sf => ({
      fahrzeug_id: sf.fahrzeug_id,
      funktion_kuerzel: sf.funktion_kuerzel,
      funktion_name: sf.funktion_name,
      fahrzeug_name: sf.fahrzeug_name,
    }))
  );

  const haupt = existingData.hauptfunktion;
  const hauptDisplayKuerzel = (() => {
    if (/\d$/.test(haupt.funktion_kuerzel || '')) return haupt.funktion_kuerzel;
    const nummer = fahrzeugTypNummern?.[haupt.fahrzeug_id] || '';
    return nummer ? `${haupt.funktion_kuerzel}${nummer}` : haupt.funktion_kuerzel;
  })();

  const removeSpringer = (key) => {
    setDraftSpringer(prev => prev.filter(s => sfKey(s) !== key));
  };

  const addSpringer = (fahrzeug, funktion) => {
    const sf = {
      fahrzeug_id: fahrzeug.id,
      funktion_kuerzel: funktion.kuerzel,
      funktion_name: funktion.name,
      fahrzeug_name: fahrzeug.funkrufname,
    };
    if (draftSpringer.some(s => sfKey(s) === sfKey(sf))) {
      toast.error('Springer-Funktion bereits zugewiesen');
      return;
    }
    setDraftSpringer(prev => [...prev, sf]);
  };

  const hasChanges = (() => {
    const oldKeys = (existingData.sprungfunktionen || []).map(sfKey).sort();
    const newKeys = draftSpringer.map(sfKey).sort();
    return JSON.stringify(oldKeys) !== JSON.stringify(newKeys);
  })();

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave({
        type: 'funktion_mit_springer',
        mitarbeiter_id: mitarbeiter.id,
        datum: diensttag.datum,
        dienst_art: diensttag.dienst_art,
        fahrzeug_id: haupt.fahrzeug_id,
        funktion_kuerzel: haupt.funktion_kuerzel,
        springer_funktionen: draftSpringer.map(s => ({
          fahrzeug_id: s.fahrzeug_id,
          funktion_kuerzel: s.funktion_kuerzel,
        })),
        ist_praktikant: istPraktikant,
      });
      toast.success('Springer aktualisiert');
      onClose();
    } catch (error) {
      console.error('Springer save error:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="springer-edit-section">
      {/* Aktuelle Hauptfunktion */}
      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
        <div className="flex items-center gap-2 mb-2">
          <CheckCircle2 className="w-4 h-4 text-green-700" />
          <span className="font-semibold text-green-900 text-sm">Aktuelle Einteilung</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge className="bg-green-600 font-mono">{hauptDisplayKuerzel}</Badge>
          <span className="text-sm text-green-800">{haupt.fahrzeug_name}</span>
        </div>

        {/* Springer mit Lösch-Funktion */}
        {draftSpringer.length > 0 && (
          <div className="mt-3 pt-3 border-t border-green-200">
            <p className="text-xs text-green-700 mb-2">Springer-Funktionen ({draftSpringer.length}):</p>
            <div className="flex flex-wrap gap-1.5">
              {draftSpringer.map((sf) => (
                <div
                  key={sfKey(sf)}
                  className="flex items-center gap-1 px-2 py-1 bg-blue-100 border border-blue-300 rounded-md text-xs"
                  data-testid={`draft-springer-${sf.fahrzeug_id}-${sf.funktion_kuerzel}`}
                >
                  <span className="font-mono font-bold text-blue-900">{sf.funktion_kuerzel}</span>
                  <span className="text-blue-800">{sf.fahrzeug_name?.split(' ')[0]}</span>
                  <button
                    type="button"
                    onClick={() => removeSpringer(sfKey(sf))}
                    className="ml-1 text-red-500 hover:text-red-700"
                    data-testid={`remove-springer-${sf.fahrzeug_id}-${sf.funktion_kuerzel}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Springer anhängen */}
      <div className="space-y-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setPanelOpen(prev => !prev)}
          className="w-full justify-start gap-2"
          data-testid="btn-toggle-springer-panel"
        >
          <Plus className="w-4 h-4" />
          {isPanelOpen ? 'Springer-Auswahl schließen' : 'Springer-Funktion anhängen'}
        </Button>

        {isPanelOpen && (
          <div className="border rounded-lg p-2 space-y-2 bg-muted/30">
            {springerFahrzeuge.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-2">
                Keine besprungbaren Fahrzeuge verfügbar
              </p>
            ) : (
              springerFahrzeuge.map(fz => (
                <div key={fz.id} className="border rounded-md p-2 bg-background">
                  <div className="flex items-center gap-2 mb-1.5">
                    <Truck className="w-3.5 h-3.5 text-slate-600" />
                    <span className="text-sm font-semibold">{fz.funkrufname}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(fz.funktionen || []).map((fn) => {
                      const isSelected = draftSpringer.some(s =>
                        s.fahrzeug_id === fz.id && s.funktion_kuerzel === fn.kuerzel
                      );
                      const sperre = istPraktikant ? null : (mitarbeiter?.gesperrte_funktionen || [])
                        .find(g => g.fahrzeug_id === fz.id && g.funktion_kuerzel === fn.kuerzel);
                      return (
                        <Button
                          key={fn.kuerzel}
                          type="button"
                          variant={isSelected ? 'default' : 'outline'}
                          size="sm"
                          disabled={!!sperre}
                          title={sperre ? (sperre.grund ? `Keine Freigabe — ${sperre.grund}` : 'Keine Freigabe') : undefined}
                          className={cn(
                            'h-7 text-xs gap-1',
                            isSelected && !sperre && 'bg-blue-600 hover:bg-blue-700',
                            sperre && 'bg-red-100 !text-red-700 opacity-70 line-through'
                          )}
                          onClick={() => {
                            if (sperre) return;
                            if (isSelected) {
                              removeSpringer(`${fz.id}_${fn.kuerzel}`);
                            } else {
                              addSpringer(fz, fn);
                            }
                          }}
                          data-testid={`btn-springer-${fz.id}-${fn.kuerzel}`}
                        >
                          {sperre && <span className="text-[9px]">⊘</span>}
                          {fn.kuerzel}
                          {isSelected && !sperre && <X className="w-3 h-3" />}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {hasChanges && (
        <Button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="w-full"
          data-testid="btn-save-springer-edit"
        >
          {isSaving ? 'Speichern...' : 'Änderungen speichern'}
        </Button>
      )}

      <div className="text-xs text-muted-foreground text-center pt-2 border-t">
        Hauptfunktion ändern: unten ein neues Fahrzeug/Funktion wählen.
      </div>
    </div>
  );
}

export default SpringerEditSection;
